import { type ComponentProps, type ReactNode } from 'react';
import { Minus, TrendingDown, TrendingUp } from 'lucide-react';

import { cn } from '@/util/utils';

type MetricFigureProps = Omit<ComponentProps<'div'>, 'children'> & {
    label: ReactNode;
    value: ReactNode;
    /** The change, already formatted ("+12.4%"). */
    delta?: ReactNode;
    deltaTone?: 'up' | 'down' | 'flat';
    /** What the delta is against ("vs last month"). */
    deltaNote?: ReactNode;
};

const TONE = {
    up: { className: 'text-success', Icon: TrendingUp },
    down: { className: 'text-destructive', Icon: TrendingDown },
    flat: { className: 'text-muted-foreground', Icon: Minus },
} as const;

/** metric-figure: the big revenue number in the display face, with its delta line. */
function MetricFigure({
    label,
    value,
    delta,
    deltaTone = 'up',
    deltaNote,
    className,
    ...props
}: MetricFigureProps) {
    const tone = TONE[deltaTone];
    return (
        <div
            data-slot="metric-figure"
            className={cn('flex min-w-0 flex-col gap-1', className)}
            {...props}
        >
            <span className="text-label-sm text-muted-foreground">{label}</span>
            <span data-slot="metric-figure-value" className="text-metric text-foreground">
                {value}
            </span>
            {delta !== undefined ? (
                <span className="mt-1 flex items-center gap-1 text-sm">
                    <span
                        data-slot="metric-figure-delta"
                        className={cn('inline-flex items-center gap-1 font-medium', tone.className)}
                    >
                        <tone.Icon aria-hidden className="size-3.5" />
                        {delta}
                    </span>
                    {deltaNote ? (
                        <span className="text-muted-foreground ml-1 text-xs">{deltaNote}</span>
                    ) : null}
                </span>
            ) : null}
        </div>
    );
}

export { MetricFigure, type MetricFigureProps };
